import React, { forwardRef, useCallback, useState } from 'react';
import cn from 'classnames';

import inputStyles from '../input/input.module.scss';
import styles from './input-area.module.scss';
import { isSome, Maybe } from '@crocodille/util';
import { utilStyles } from '@styles';
import { FormField } from '@crocodille/common';

type FieldProps = 'value' | 'onChange' | 'disabled' | 'onBlur';

export function fieldToInputAreaProps(
  field: Pick<FormField<Maybe<string>>, 'error' | FieldProps>,
) {
  return {
    invalid: isSome(field.error),
    value: field.value === null ? '' : field.value,
    onChange: field.onChange,
    onBlur: field.onBlur,
    disabled: field.disabled,
  } satisfies Pick<InputAreaProps, 'invalid' | FieldProps>;
}

export type InputAreaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange'
> & {
  readonly label?: string;
  readonly labelHidden?: boolean;
  readonly onChange?: (value: string) => void;
  readonly invalid?: boolean;
};

const InputArea = forwardRef(
  (
    {
      className,
      onChange,
      label,
      labelHidden = false,
      onFocus,
      onBlur,
      value,
      required,
      invalid = false,
      ...props
    }: InputAreaProps,
    ref: React.ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
    };

    const [focused, setFocused] = useState(false);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setFocused(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setFocused(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    return (
      <label
        className={cn(
          inputStyles.container,
          {
            [inputStyles.isFilled]:
              isSome(value) && (typeof value !== 'string' || value.length > 0),
            [inputStyles.isFocused]: focused,
            [inputStyles.isRequired]: required,
            [inputStyles.isInvalid]: invalid,
          },
          className,
        )}
      >
        {label ? (
          <span
            className={cn({
              [inputStyles.label]: !labelHidden,
              [utilStyles.screenReaderOnly]: labelHidden,
            })}
          >
            {label}
          </span>
        ) : null}
        <textarea
          ref={ref}
          className={cn(inputStyles.input, styles.input)}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          required={required}
          {...props}
        />
      </label>
    );
  },
);

InputArea.displayName = 'InputArea';

export default InputArea;
